"use client";

import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * A callback with a stable identity that always invokes the latest closure.
 *
 * Lets a handler be listed in a `useMemo`/`React.memo` dependency array without
 * busting it every render — the returned function never changes identity, but
 * calls through to the most recent `fn` (refreshed after each commit). Use only
 * for event handlers (never call the result during render). This is the common
 * "event callback" pattern that React's upcoming `useEffectEvent` formalises.
 */
export function useStableCallback<A extends unknown[], R>(fn: (...args: A) => R) {
  const ref = useRef(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  });
  return useCallback((...args: A) => ref.current(...args), []);
}
